import { useEffect, useSyncExternalStore } from 'react';
import type React from 'react';

import { useLocation } from 'react-router';

import { useCaptchaHostStoreContext } from '#src/app/shared/hooks/perimeterX';

import { PerimeterXCaptchaModal } from './PerimeterXCaptchaModal';

/**
 * Global host for the imperative PerimeterX captcha (see
 * docs/architecture/decisions/2026-06-18-perimeterx-captcha-global-host.md).
 * Mounted once in the app, it renders the captcha modal whenever a challenge
 * is opened via usePerimeterXCaptcha(), and renders null while idle.
 */

export const PerimeterXCaptchaHost: React.FC = () => {
	const captchaHostStore = useCaptchaHostStoreContext();
	const state = useSyncExternalStore(
		(onStoreChange) => captchaHostStore.subscribe(onStoreChange),
		() => captchaHostStore.getSnapshot(),
		() => captchaHostStore.getServerSnapshot(),
	);
	const location = useLocation();

	// settle any pending challenge on unmount so no awaiting caller can hang
	useEffect(() => {
		return () => {
			captchaHostStore.dismiss();
		};
	}, []);

	// dismiss a pending challenge on navigation, since this host outlives route changes;
	// depend on the location's own string fields rather than the location object itself,
	// since its identity isn't stable across re-renders unrelated to navigation (e.g. this
	// component's own state changes), which would otherwise dismiss a challenge right as it opens
	const { pathname, search, hash } = location;
	useEffect(() => {
		captchaHostStore.dismiss();
	}, [pathname, search, hash]);

	if (state.status !== 'pending') return null;

	return (
		<PerimeterXCaptchaModal
			key={state.challengeId}
			isOpen
			setIsOpen={(isOpen) => {
				if (!isOpen) captchaHostStore.dismiss();
			}}
			pxResponse={state.pxResponse}
			onResult={(result) => {
				captchaHostStore.resolve(result);
			}}
		/>
	);
};
